"use client";

import * as React from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import Image from "next/image";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetTitle,
  SheetClose,
} from "@/components/sheet/sheet";
import { Button } from "@/components/button/button/button";
import { User, X } from "lucide-react";
import { toCmsHref } from "@/lib/cmsHelpers";
import styles from "./Navbar.module.css";

type NavLink = {
  id?: number;
  label: string;
  href: string;
  newTab?: boolean;
  icon?: string | null;
  order?: number;
};

type CTAButton = {
  id?: number;
  copy: string;
  title: string;
  url: string;
  type: string;
  target: string;
  button_customID?: string;
  size?: string;
};

interface LogoSource {
  url: string;
  width?: number;
  height?: number;
}

/* logoLight/logoDark are still accepted (the layout passes CMS logos)
   but unused — the logo is a Druk Wide text wordmark now. */
interface NavbarProps {
  navL1?: NavLink[];
  ctaButtons?: CTAButton[];
  logoLight?: LogoSource;
  logoDark?: LogoSource;
}

const Navbar = ({ navL1, ctaButtons }: NavbarProps) => {
  const [isScrolled, setIsScrolled] = React.useState(false);
  const [isDark, setIsDark] = React.useState(false);
  const pathname = usePathname();
  // The bar itself is always visible; only the LOGO hides while the HOMEPAGE
  // hero is in view (that hero carries its own big wordmark) and fades in
  // once the user scrolls past it. Every other page always shows the logo —
  // /download and /launch mark their card sections as heroes too, but those
  // heroes have no wordmark, so hiding there would leave the bar empty.
  const isHome = pathname === "/";
  // Seeded hidden on the homepage so the first paint already matches what the
  // scroll handler would decide. Initializing to `false` made the logo flash
  // in and then vanish once the effect ran after hydration. `usePathname` is
  // available during SSR, so the server markup agrees and hydration is clean.
  const [logoHidden, setLogoHidden] = React.useState(isHome);

  // Handle hash link clicks — scroll to section if already on the target page
  const handleHashClick = (e: React.MouseEvent, href: string) => {
    const hashIndex = href.indexOf("#");
    if (hashIndex === -1) return; // not a hash link, let Next.js handle it

    const targetPath = href.slice(0, hashIndex) || "/";
    const hash = href.slice(hashIndex);

    // If we're already on the target page, scroll to the element directly
    if (pathname === targetPath) {
      e.preventDefault();
      const el = document.querySelector(hash);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      }
      // Update URL hash without navigation
      window.history.pushState(null, "", href);
    }
    // Otherwise let Next.js navigate; the browser will handle the hash scroll on load
  };

  const resolveHref = (href: string): string => {
    if (!href) return href;
    // If this is just a hash (e.g. "#features"), send the user to the homepage section.
    if (href.startsWith("#")) {
      return `/${href}`;
    }
    // Normalize CMS-stored values: editors may enter `about` or `/about` —
    // `<Link>` only treats leading-slash paths as absolute, so coerce.
    return toCmsHref(href) || href;
  };

  React.useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
      // Re-query per scroll tick: cheap, and robust against the hero
      // mounting after the navbar (CMS-driven content). The logo reveals
      // once only the last 30% of the hero remains above the navbar,
      // rather than waiting for the hero to fully pass.
      const hero = isHome ? document.querySelector("[data-hero-section]") : null;
      if (hero) {
        const r = hero.getBoundingClientRect();
        setLogoHidden(r.bottom - r.height * 0.3 > 64);
      } else {
        setLogoHidden(false);
      }
    };

    const handleThemeChange = () => {
      setIsDark(document.documentElement.classList.contains('dark'));
    };

    // Initial theme check
    handleThemeChange();

    // Initial logo/scroll state (covers load mid-page and hero-less pages)
    handleScroll();

    // Set up observers
    window.addEventListener('scroll', handleScroll);

    // Watch for theme changes
    const observer = new MutationObserver(handleThemeChange);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      observer.disconnect();
    };
  }, [pathname, isHome]);

  // No CMS nav content at all -> no navbar. (Placed after the hooks so
  // the rules of hooks hold.)
  const hasNavContent =
    (navL1?.length ?? 0) > 0 || (ctaButtons?.length ?? 0) > 0;
  if (!hasNavContent) return null;

  return (
    <header className={`${styles.header} ${styles.headerMount}`}>
      {/* Full-width background bar */}
      <div className={`${styles['header-background']} ${
        !isScrolled
          ? `${styles['header-background-visible']} ${isDark ? styles['header-background-dark'] : ''}`
          : styles['header-background-hidden']
      }`} />

      <div className={`${styles.navbar} ${
        isScrolled
          ? `${styles['navbar-scrolled']} ${isDark ? styles['navbar-scrolled-dark'] : ''}`
          : `${styles['navbar-default']} ${isDark ? styles['navbar-default-dark'] : ''}`
      }`}>
        {/* Dark theme background wrapper - inside navbar */}
        {isDark && (
          <div className={styles['dark-bg']} />
        )}
        {/* Desktop Nav - Left of logo */}
        <div className={styles['nav-section']}>
          <nav className={styles['nav-desktop']}>
            {/* CMS-driven only (NavBar global) — no hardcoded fallbacks */}
            {(navL1 ?? []).map((link) => (
              <Link
                key={link.id ?? link.href ?? link.label}
                href={resolveHref(link.href)}
                className={styles['nav-link']}
                scroll={!link.href.includes("#")}
                onClick={(e) => handleHashClick(e, resolveHref(link.href))}
                target={link.newTab ? "_blank" : undefined}
                rel={link.newTab ? "noopener noreferrer" : undefined}
              >
                {link.label}
              </Link>
            ))}
          </nav>
        </div>
        {/* Logo - Center */}
        <div className={styles['logo-section']}>
          <Link
            href="/"
            className={`${styles['logo-link']} ${logoHidden ? styles['logo-link-hidden'] : ''}`}
            aria-hidden={logoHidden}
            tabIndex={logoHidden ? -1 : undefined}
          >
            <div className={styles['logo-container']}>
              {/* Druk Wide text wordmark — replaces the image logos */}
              <span className={styles['logo-wordmark']}>Auto Screenshot</span>
            </div>
          </Link>
        </div>

        {/* CTA Buttons - Right Side */}
        <div className={styles['cta-section']}>
          {/* Desktop CTA Buttons */}
          <div className={styles['cta-desktop']}>
            {ctaButtons && ctaButtons.length > 0 && ctaButtons.map((btn) => {
              const customId = btn.button_customID?.toLowerCase().trim();
              const isDownloadButton = customId === "download";
              const isEmailLinkButton = customId === "email";
              const isPrimary = btn.type === "primary";
              const rawHref = (btn.url || "").trim();
              const emailHref =
                isEmailLinkButton && rawHref
                  ? rawHref.toLowerCase().startsWith("mailto:")
                    ? rawHref
                    : `mailto:${rawHref}`
                  : "";

              const downloadIcon = isDownloadButton ? (
                <Image
                  src="/misc/apple-logo.svg"
                  alt=""
                  width={14}
                  height={17}
                  className={styles['cta-icon']}
                  aria-hidden="true"
                />
              ) : null;

              // Account: icon-only button showing the Lucide User icon,
              // centered (customID "account")
              if (customId === "account") {
                const isBlank = btn.target === "blank";
                return (
                  <Button
                    key={btn.id ?? btn.url}
                    asChild
                    variant="ghost"
                    size="icon"
                    className={styles['account-trigger']}
                  >
                    <Link
                      href={btn.url || "#"}
                      target={isBlank ? "_blank" : undefined}
                      rel={isBlank ? "noopener noreferrer" : undefined}
                      aria-label={btn.copy || "Account"}
                    >
                      <User className={styles['account-icon']} aria-hidden="true" />
                    </Link>
                  </Button>
                );
              }

              // Non-primary buttons render as plain nav links (like "Sign In" on main)
              if (!isPrimary) {
                const href = isEmailLinkButton && emailHref ? emailHref : (btn.url || "#");
                const isBlank = btn.target === "blank";
                return (
                  <Link
                    key={btn.id ?? btn.url}
                    href={href}
                    target={isBlank ? "_blank" : undefined}
                    rel={isBlank ? "noopener noreferrer" : undefined}
                    className={styles['nav-link']}
                  >
                    {btn.copy}
                  </Link>
                );
              }

              // Primary button variant changes on scroll: subtle at top, filled when scrolled
              const variant = isScrolled ? "primary" as const : "default" as const;

              // Primary CTA: navigate using CMS-provided URL
              return (
                <Button
                  key={btn.id ?? btn.url}
                  asChild
                  variant={variant}
                  size="md"
                >
                  <Link
                    href={btn.url}
                    target={btn.target === "blank" ? "_blank" : undefined}
                    rel={
                      btn.target === "blank"
                        ? "noopener noreferrer"
                        : undefined
                    }
                  >
                    {downloadIcon}
                    <span>{btn.copy}</span>
                  </Link>
                </Button>
              );
            })}
          </div>

          {/* Mobile Menu Button */}
          <div className={styles['mobile-menu-wrap']}>
          <Sheet>
            <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className={styles['hamburger-trigger']}>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles['hamburger-icon']}>
                  <line x1="4" x2="20" y1="12" y2="12" />
                  <line x1="4" x2="20" y1="6" y2="6" />
                  <line x1="4" x2="20" y1="18" y2="18" />
                </svg>
                <span className={styles['sr-only']}>Toggle menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent
              side="right"
              hideClose
              className={styles['sheet-content']}
            >
              <SheetTitle className={styles['sr-only']}>Navigation Menu</SheetTitle>

              {/* Custom header with close button on left */}
              <div className={styles['sheet-close-row']}>
                <SheetClose asChild>
                  <Button variant="ghost" size="icon" className={styles['sheet-close-btn']}>
                    <X className={styles['sheet-close-icon']} />
                    <span className={styles['sr-only']}>Close menu</span>
                  </Button>
                </SheetClose>
              </div>

              <div className={styles['mobile-nav']}>
                {/* CMS-driven only (NavBar global) — no hardcoded fallbacks */}
                {(navL1 ?? []).map((link) => (
                  <div className={styles['mobile-nav-item']} key={link.id ?? link.href ?? link.label}>
                    <SheetClose asChild>
                      <Link
                        href={resolveHref(link.href)}
                        className={styles['mobile-nav-link']}
                        scroll={!link.href.includes("#")}
                        onClick={(e) => handleHashClick(e, resolveHref(link.href))}
                        target={link.newTab ? "_blank" : undefined}
                        rel={link.newTab ? "noopener noreferrer" : undefined}
                      >
                        {link.label}
                      </Link>
                    </SheetClose>
                  </div>
                ))}

                <div className={styles['mobile-cta-group']}>
                  {/* CTA buttons — primary renders first in the flyout so
                      "Sign in" (or any secondary/ghost CTA) sits beneath
                      the main action, regardless of CMS authoring order. */}
                  {ctaButtons && ctaButtons.length > 0 && [...ctaButtons]
                    .sort((a, b) => {
                      const ap = a.type === "primary" ? 0 : 1;
                      const bp = b.type === "primary" ? 0 : 1;
                      return ap - bp;
                    })
                    .map((btn) => {
                    const customId = btn.button_customID?.toLowerCase().trim();
                    const isDownloadButton = customId === "download";
                    const isAccountButton = customId === "account";
                    const isEmailLinkButton = customId === "email";
                    const rawHref = (btn.url || "").trim();
                    const emailHref =
                      isEmailLinkButton && rawHref
                        ? rawHref.toLowerCase().startsWith("mailto:")
                          ? rawHref
                          : `mailto:${rawHref}`
                        : "";
                    return (
                      <div className={styles['mobile-nav-item']} key={btn.id ?? btn.url}>
                        <SheetClose asChild>
                          {isEmailLinkButton && emailHref ? (
                            <Button
                              asChild
                              className={styles['mobile-cta-button']}
                              variant={
                                btn.type === "primary"
                                  ? "primary"
                                  : btn.type === "secondary"
                                  ? "default"
                                  : "ghost"
                              }
                              size="lg"
                            >
                              <a href={emailHref}>
                                {isDownloadButton && (
                                  <Image
                                    src="/misc/apple-logo.svg"
                                    alt=""
                                    width={14}
                                    height={17}
                                    className={styles['mobile-cta-icon']}
                                    aria-hidden="true"
                                  />
                                )}
                                <span>{btn.copy}</span>
                              </a>
                            </Button>
                          ) : (
                            <Button
                              asChild
                              className={styles['mobile-cta-button']}
                              variant={
                                btn.type === "primary"
                                  ? "primary"
                                  : btn.type === "secondary"
                                  ? "default"
                                  : "ghost"
                              }
                              size="lg"
                            >
                      <Link
                                href={btn.url}
                                target={
                                  btn.target === "blank" ? "_blank" : undefined
                                }
                                rel={
                                  btn.target === "blank"
                                    ? "noopener noreferrer"
                                    : undefined
                                }
                              >
                                {isDownloadButton && (
                                  <Image
                                    src="/misc/apple-logo.svg"
                                    alt=""
                                    width={14}
                                    height={17}
                                    className={styles['mobile-cta-icon']}
                                    aria-hidden="true"
                                  />
                                )}
                                {isAccountButton && (
                                  <User
                                    className={styles['mobile-cta-icon']}
                                    aria-hidden="true"
                                  />
                                )}
                                <span>{btn.copy}</span>
                      </Link>
                    </Button>
                          )}
                        </SheetClose>
                  </div>
                    );
                  })}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
      </div>
    </header>
  );
};

export default Navbar;
